import { useGSAP } from '@gsap/react'
import { RefObject, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import Draggable from 'gsap/dist/Draggable'
import { InertiaPlugin } from 'gsap/dist/InertiaPlugin'
import { useWindowSize } from './useWindowSize'
import { clamp } from 'utils'

export interface SliderOptions {
	refreshToken?: unknown

	/**
	 * The resistance of the slider when throwing. Default is 1000, higher numbers is stiffer
	 */
	throwResistance?: number

	/**
	 * Whether to disable dragging. Default is false.
	 */
	disableDragging?: boolean
}

interface DraggableVars extends Draggable.Vars {
	endX: number
}

export const useSlider = (options?: SliderOptions): [ref: RefObject<HTMLUListElement | null>, currentSlide: number, goToSlide: (index: number) => void] => {
	const [currentSlide, setCurrentSlide] = useState<number>(0)
	const sliderRef = useRef<HTMLUListElement>(null)
	const draggableRef = useRef<Draggable>(null)
	const snapPointsRef = useRef<number[]>([])
	const prevWindowSizeRef = useRef({ width: 0, height: 0 })
	const isInitialRef = useRef(true)

	const windowSize = useWindowSize()

	const goToClosestSnapPoint = (x: number): void => {
		let closestSnapPointIndex = 0
		let smallestDistance = Math.abs(snapPointsRef.current[0] - x)

		for (let i = 1; i < snapPointsRef.current.length; i++) {
			const distance = Math.abs(snapPointsRef.current[i] - x)
			// We use <= to go the end if it's equal
			if (distance <= smallestDistance) {
				smallestDistance = distance
				closestSnapPointIndex = i
			}
		}

		setCurrentSlide(closestSnapPointIndex)
	}

	// Create and initialize draggable instance
	const initDraggable = () => {
		if (!sliderRef.current) return

		const slider = sliderRef.current
		const children = Array.from(slider.children)

		if (children.length === 0) return

		// Reset any transform before calculating bounds
		gsap.set(slider, { clearProps: 'x' })

		const leftMostLeftBound = children[0].getBoundingClientRect().left
		const rightMostRightBound = children[children.length - 1].getBoundingClientRect().right
		const sliderRightBound = leftMostLeftBound - rightMostRightBound + slider.clientWidth

		snapPointsRef.current = children.map((child) => clamp(leftMostLeftBound - child.getBoundingClientRect().left, sliderRightBound, leftMostLeftBound))

		// Kill previous draggable instance if it exists
		if (draggableRef.current) {
			draggableRef.current.kill()
		}

		draggableRef.current = Draggable.create(slider, {
			type: 'x',
			inertia: true,
			snap: snapPointsRef.current,
			zIndexBoost: false,
			throwResistance: options?.throwResistance ?? 1000,
			bounds: {
				maxX: 0,
				minX: sliderRightBound,
			},
			onDragEnd: function () {
				/*
				`this` refers to the Draggable here.
				From the gsap documentation:
				The ending x (horizontal) position of the Draggable instance
				which is calculated as soon as the mouse/touch is released
				after a drag, meaning you can use it to
				predict precisely where it'll land after an inertia flick.
				 */
				// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
				goToClosestSnapPoint((this as DraggableVars).endX)
			},
		})[0] // Draggable.create returns an array

		if (options?.disableDragging !== undefined && options.disableDragging) {
			draggableRef.current.disable()
		}

		// Update slider position to match current slide
		updateSliderPosition(0)
	}

	// Update the slider position
	const updateSliderPosition = (duration = 0.5): void => {
		if (!sliderRef.current || snapPointsRef.current.length === 0) return

		// Make sure currentSlide is within bounds
		const validSlideIndex = Math.max(0, Math.min(currentSlide, snapPointsRef.current.length - 1))
		const newPosX = snapPointsRef.current[validSlideIndex]

		if (typeof newPosX === 'number') {
			if (duration === 0) {
				gsap.set(sliderRef.current, { x: newPosX })
			} else {
				gsap.to(sliderRef.current, { duration, x: newPosX })
			}
		}
	}

	// Initialize on first render
	useGSAP(() => {
		// Must register plugins before use
		gsap.registerPlugin(Draggable, InertiaPlugin)
		initDraggable()
		isInitialRef.current = false
	}, [options?.refreshToken])

	// Handle window resize
	useEffect(() => {
		// Skip first render since useGSAP already handles it
		if (isInitialRef.current) {
			return
		}

		const hasWindowSizeChanged = prevWindowSizeRef.current.width !== windowSize.width || prevWindowSizeRef.current.height !== windowSize.height

		if (hasWindowSizeChanged && prevWindowSizeRef.current.width !== 0) {
			// Temporarily reset the transform to properly recalculate bounds
			if (sliderRef.current) {
				gsap.set(sliderRef.current, { clearProps: 'x' })
			}

			// Reinitialize draggable with new dimensions
			initDraggable()

			// Force immediate position update without animation
			updateSliderPosition(0)
		}

		// Update the previous window size
		prevWindowSizeRef.current = { ...windowSize }
	}, [windowSize])

	// Update position when current slide changes
	useEffect(() => {
		// Ensure we don't update the position if a throw animation is currently in progress
		if (!draggableRef.current || draggableRef.current.isThrowing) return

		updateSliderPosition()
	}, [currentSlide])

	// enable or disable dragging if options.disableDragging changes
	useEffect(() => {
		if (!draggableRef.current) return
		if (options?.disableDragging !== undefined && options.disableDragging) {
			draggableRef.current.disable()
		} else {
			draggableRef.current.enable()
		}
	}, [options?.disableDragging])

	const goToSlide = (slideIndex: number) => {
		if (!draggableRef.current) return

		// GSAP types are wrong, tween can be undefined
		const tween = (draggableRef.current as Partial<Draggable>).tween

		if (tween) {
			tween.kill()
		}

		// Ensure slideIndex is within bounds
		const validSlideIndex = Math.max(0, Math.min(slideIndex, snapPointsRef.current.length - 1))
		setCurrentSlide(validSlideIndex)
	}

	return [sliderRef, currentSlide, goToSlide]
}
